'use client';

/**
 * 블로그 글(/blog/**, 단 /blog 루트 제외) 세션 내 이동을 sessionStorage 스택으로 추적합니다.
 * router.back 결과는 pathname 변경으로 근사(뒤로가기 시 바로 아래 경로와 일치하면 pop).
 */

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

export const BLOG_NAV_STACK_KEY = 'tindlo_blog_nav_stack';

/** layout·back 버튼에서 공통 사용 — 스택 깊이 2 이상이면 글 간 SPA 이동이 있었다고 간주 */
export function readBlogNavigationStack(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = sessionStorage.getItem(BLOG_NAV_STACK_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;

    return Array.isArray(parsed)
      ? (parsed.filter((x): x is string => typeof x === 'string')).slice(-20)
      : [];
  } catch {
    return [];
  }
}

function writeStack(stack: string[]) {
  try {
    sessionStorage.setItem(BLOG_NAV_STACK_KEY, JSON.stringify(stack.slice(-20)));
  } catch {
    /* noop */
  }
}

function isBlogArticlePath(path: string): boolean {
  if (path === '/blog') {
    return false;
  }

  return path.startsWith('/blog/');
}

function isSameSiteReferrer(referrerUrl: string, origin: string): boolean {
  try {
    return new URL(referrerUrl).origin === origin;
  } catch {
    return false;
  }
}

/**
 * 상단·하단 뒤로가기 공통: 스택 깊이 2+면 히스토리 back, referrer가 동일 사이트면 back, 아니면 /blog
 */
export function navigateBlogPreferredBack(router: { back: () => void; push: (href: string) => void }) {
  const stack = readBlogNavigationStack();

  if (stack.length >= 2) {
    router.back();

    return;
  }

  if (typeof window === 'undefined') {
    router.push('/blog');

    return;
  }

  const origin = window.location.origin;

  const ref =
    typeof document !== 'undefined' && typeof document.referrer === 'string' ? document.referrer : '';

  if (ref && isSameSiteReferrer(ref, origin)) {
    router.back();

    return;
  }

  router.push('/blog');
}

export function BlogNavStackTelemetry() {
  const pathname = usePathname();
  /** 전체 새로고침마다 referrer-only 판별에 쓰이는 깨진 스택 제거 후 재생성하기 위함 */
  const didHydrateReload = useRef(false);

  useEffect(() => {
    if (didHydrateReload.current) {
      return;
    }

    didHydrateReload.current = true;

    try {
      const navigation =
        typeof performance !== 'undefined' ? performance.getEntriesByType?.('navigation')?.[0] : undefined;

      const navType =
        navigation && navigation.entryType === 'navigation' ? (navigation as PerformanceNavigationTiming).type : undefined;

      if (navType === 'reload') {
        sessionStorage.removeItem(BLOG_NAV_STACK_KEY);
      }
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (!pathname) {
      return;
    }

    /** 블로그 밖으로 나가면 스택 무효 */
    if (!pathname.startsWith('/blog')) {
      sessionStorage.removeItem(BLOG_NAV_STACK_KEY);

      return;
    }

    /** 목록 페이지 /blog 진입 시 이전 글 스택 초기화(목록 기준 새 흐름) */
    if (pathname === '/blog') {
      sessionStorage.removeItem(BLOG_NAV_STACK_KEY);

      return;
    }

    if (!isBlogArticlePath(pathname)) {
      return;
    }

    const stack = readBlogNavigationStack();
    const last = stack[stack.length - 1];

    if (!last) {
      writeStack([pathname]);

      return;
    }

    if (pathname === last) {
      return;
    }

    if (stack.length >= 2 && pathname === stack[stack.length - 2]) {
      stack.pop();
      writeStack(stack);

      return;
    }

    stack.push(pathname);
    writeStack(stack);
  }, [pathname]);

  return null;
}
